import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) nav("/app"); }, [user]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) { localStorage.setItem("wk_token", t); window.location.href = "/app"; return; }
    const err = params.get("sso_error");
    if (err === "nouser") toast.error("Tu cuenta de Microsoft no está registrada en el ERP (o está inactiva). Pide al administrador que dé de alta tu correo.", { duration: 9000 });
    else if (err === "tenant") toast.error("Esa organización de Microsoft no está autorizada para este ERP.", { duration: 9000 });
    else if (err === "state") toast.error("Se perdió la sesión de inicio (posible bloqueo de cookies del navegador). Vuelve a intentarlo.", { duration: 9000 });
    else if (err === "provider") toast.error("Microsoft canceló o no completó el inicio de sesión.", { duration: 9000 });
    else if (err === "token") toast.error("No se pudo validar la respuesta de Microsoft. Intenta de nuevo.", { duration: 9000 });
    else if (err) toast.error("No se pudo iniciar sesión con Microsoft.", { duration: 9000 });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) { toast.success("Bienvenido"); nav("/app"); }
    else toast.error(res.error);
  };

  const microsoft = () => {
    // Navigate directly (top-level navigation). The backend guards the config;
    // this avoids a stale/failed status fetch (e.g. Safari cache) blocking SSO.
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/login`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12">
        <Link to="/" className="flex items-center gap-2"><div className="w-9 h-9 bg-white text-primary grid place-items-center font-heading font-bold rounded-md">W</div><span className="font-heading font-bold text-lg">West KAC ERP</span></Link>
        <div>
          <h2 className="text-4xl font-heading font-bold tracking-tight mb-4">Centraliza tu operación comercial.</h2>
          <p className="text-white/60 max-w-md">CRM, cotizaciones, pedidos, logística y finanzas en una sola plataforma.</p>
        </div>
        <div className="text-xs text-white/40">© 2026 West KAC Company</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-8"><ArrowLeft className="w-4 h-4" /> Volver al sitio</Link>
          <h1 className="text-3xl font-heading font-bold tracking-tight mb-2">Iniciar sesión</h1>
          <p className="text-muted-foreground mb-8 text-sm">Accede al portal interno con tu cuenta corporativa.</p>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Correo corporativo</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="tu@west-kac.com" /></div>
            <div><Label>Contraseña</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" /></div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">{loading ? "Ingresando..." : "Ingresar"}</Button>
          </form>
          <div className="my-6 flex items-center gap-3"><div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">o</span><div className="flex-1 h-px bg-border" /></div>
          <Button variant="outline" className="w-full" onClick={microsoft} data-testid="login-microsoft">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 23 23"><path fill="#f35325" d="M1 1h10v10H1z"/><path fill="#81bc06" d="M12 1h10v10H12z"/><path fill="#05a6f0" d="M1 12h10v10H1z"/><path fill="#ffba08" d="M12 12h10v10H12z"/></svg>
            Iniciar sesión con Microsoft 365
          </Button>
        </div>
      </div>
    </div>
  );
}
